/* eslint-disable */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Eye, EyeOff } from "lucide-react";
import PasswordChangePromptModal from "@/components/common/PasswordChangePromptModal";  
import { authService } from "@/services/auth.service";

type ProfileModalProps = {
  open: boolean;
  onClose: () => void;
  onLogout: () => void;
  userId?: string;
  defaultName?: string | null;
  defaultEmail?: string | null;
  defaultPhone?: string | null;
  defaultCompany?: string | null;
  initialMenu?: 'profile' | 'personalization';
};

export default function ProfileModal({ open, onClose, onLogout, userId, defaultName, defaultEmail, defaultPhone, defaultCompany, initialMenu }: ProfileModalProps) {
  const [email, setEmail] = useState<string>(defaultEmail ?? "");
  const [name, setName] = useState<string>(defaultName ?? "");
  const [phone, setPhone] = useState<string>(defaultPhone ?? "");
  const [company, setCompany] = useState<string>(defaultCompany ?? "");
  const [originalEmail, setOriginalEmail] = useState<string>(defaultEmail ?? "");
  const [emailCheckedFor, setEmailCheckedFor] = useState<string>("");
  const [emailCheckOk, setEmailCheckOk] = useState<boolean>(true);
  const [emailCheckTouched, setEmailCheckTouched] = useState<boolean>(false);

  const storageKeyPersonalOptIn = useMemo(() => `dw_user_${userId ?? "me"}_personal_opt_in`, [userId]);
  const storageKeyEmailOptIn = useMemo(() => `dw_user_${userId ?? "me"}_email_opt_in`, [userId]);
  const storageKeyProfileName = useMemo(() => `dw_user_${userId ?? "me"}_profile_name`, [userId]);
  const storageKeyProfileEmail = useMemo(() => `dw_user_${userId ?? "me"}_profile_email`, [userId]);
  const storageKeyProfilePhone = useMemo(() => `dw_user_${userId ?? "me"}_profile_phone`, [userId]);
  const storageKeyProfileCompany = useMemo(() => `dw_user_${userId ?? "me"}_profile_company`, [userId]);

  const [personalAgree, setPersonalAgree] = useState<boolean>(false);
  const [emailSubscribe, setEmailSubscribe] = useState<boolean>(false);
  const [showChangePw, setShowChangePw] = useState<boolean>(false);
  const [phoneTouched, setPhoneTouched] = useState<boolean>(false);
  const isPhoneValid = useMemo(() => /^010\d{8}$/.test(phone), [phone]);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [currentPwTouched, setCurrentPwTouched] = useState<boolean>(false);
  const isCurrentPwValid = useMemo(() => currentPassword.trim().length > 0, [currentPassword]);
  const [pwVisible, setPwVisible] = useState<boolean>(false);
  const [pwMismatch, setPwMismatch] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [savingMessage, setSavingMessage] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<'profile' | 'personalization'>('profile');
  
  // 터치 제스처 상태
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [pzJob, setPzJob] = useState<string | null>(null); // job code 저장 (예: "DEV")
  const [pzInterests, setPzInterests] = useState<Set<string>>(new Set()); // interest code 저장 (예: "commerce-trend")
  const [pzDropdownOpen, setPzDropdownOpen] = useState<boolean>(false);
  const pzDropdownRef = useRef<HTMLDivElement | null>(null);
  
  // API에서 가져온 직무 및 관심사 목록
  const [jobList, setJobList] = useState<Array<{ code: string; codeName: string }>>([]);
  const [interestList, setInterestList] = useState<Array<{ code: string; valueText: string }>>([]);
  
  // 코드 → 직무명 매핑 (표시용)
  const jobCodeToName = useMemo<Record<string, string>>(() => {
    const mapping: Record<string, string> = {};
    jobList.forEach(item => {
      mapping[item.code] = item.codeName;
    });
    return mapping;
  }, [jobList]);
  
  // 코드 → 라벨 매핑 (API에서 가져온 interestList 기반)
  const interestCodeToLabel = useMemo<Record<string, string>>(() => {
    const mapping: Record<string, string> = {};
    interestList.forEach(item => {
      mapping[item.code] = item.valueText;
    });
    return mapping;
  }, [interestList]);
  
  // 라벨 → 코드 매핑 (저장 시 사용)
  const interestLabelToCode = useMemo<Record<string, string>>(() => {
    const mapping: Record<string, string> = {};
    interestList.forEach(item => {
      mapping[item.valueText] = item.code;
    });
    return mapping;
  }, [interestList]);

  useEffect(() => {
    if (!open) return;
    try {
      const p = localStorage.getItem(storageKeyPersonalOptIn);
      const e = localStorage.getItem(storageKeyEmailOptIn);
      if (p !== null) setPersonalAgree(p === "1");
      if (e !== null) setEmailSubscribe(e === "1");
      // 서버 세션에서 실제 사용자 기본값 로드
      (async () => {
        try {
          const data = await authService.getSession();
          // To-Be 형식 처리
          if (data?.code === "SUCCESS" && data?.data) {
            const user = data.data;
            if (typeof user.name === 'string') setName(user.name);
            if (typeof user.email === 'string') {
              setEmail(user.email);
              setOriginalEmail(user.email);
              setEmailCheckedFor(user.email);
              setEmailCheckOk(true);
            }
            if (typeof user.phone === 'string') setPhone((user.phone || '').replace(/\D/g, ''));
            if (typeof user.company === 'string') setCompany(user.company || '');
            // TODO: emailSubscribed는 API에서 반환하도록 수정 필요
            // if (typeof data.emailSubscribed === 'boolean') setEmailSubscribe(!!data.emailSubscribed);
          }
        } catch {}
      })();
    } catch {}
    const n0 = localStorage.getItem(storageKeyProfileName);
    const em0 = localStorage.getItem(storageKeyProfileEmail);
    const ph0 = localStorage.getItem(storageKeyProfilePhone);
    const co0 = localStorage.getItem(storageKeyProfileCompany);
    if (n0 !== null) setName(n0);
    if (em0 !== null) setEmail(em0);
    if (ph0 !== null) setPhone(ph0);
    if (co0 !== null) setCompany(co0);
  }, [open, storageKeyPersonalOptIn, storageKeyEmailOptIn, storageKeyProfileName, storageKeyProfileEmail, storageKeyProfilePhone, storageKeyProfileCompany]);

  useEffect(() => {
    function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    if (open) window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  // 외부에서 초기 탭 지정 시 반영
  useEffect(() => {
    if (!open) return;
    if (initialMenu) setActiveMenu(initialMenu);
  }, [open, initialMenu]);

  // 모달이 열릴 때 직무/관심사 목록 로드
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const d = await authService.getPersonalization();
        // To-Be 형식 처리
        if (d?.code === "SUCCESS" && d?.data) {
          // 직무 및 관심사 목록 설정
          if (Array.isArray(d.data.jobList)) {
            setJobList(d.data.jobList);
          }
          if (Array.isArray(d.data.interestList)) {
            setInterestList(d.data.interestList);
          }
          
          // 현재 사용자 설정 로드 (personalization 탭일 때만)
          if (activeMenu === 'personalization') {
            // 직무: 코드로 저장 (예: "DEV")
            const job = (d.data.job ?? null) as string | null;
            // console.log('ProfileModal - 로드된 직무 코드:', job);
            setPzJob(job);
            // 관심사: interestsCode를 코드 배열로 파싱
            const intsRaw = (d.data.interestsCode ?? '') as string;
            const arr = (typeof intsRaw === 'string' ? intsRaw.split(',').map(s => s.trim()).filter(Boolean) : []) as string[];
            // 코드를 Set에 저장
            const normalized = Array.from(new Set(arr));
            // console.log('ProfileModal - 로드된 관심사 코드:', normalized);
            setPzInterests(new Set(normalized));
          }
        }
      } catch {}
    })();
  }, [open, activeMenu]);

  // personalization 탭 전환 시 클릭 이벤트 리스너 설정
  useEffect(() => {
    if (!open || activeMenu !== 'personalization') return;
    const onClickAway = (e: MouseEvent) => {
      const el = pzDropdownRef.current;
      if (!el) return;
      if (!el.contains(e.target as Node)) setPzDropdownOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') setPzDropdownOpen(false); };
    window.addEventListener('click', onClickAway);
    window.addEventListener('keydown', onEsc);
    return () => {
      window.removeEventListener('click', onClickAway);
      window.removeEventListener('keydown', onEsc);
    };
  }, [open, activeMenu]);

  const toggleInterest = (code: string) => {
    setPzInterests(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const onSavePersonalization = async () => {
    try {
      setSaving(true);
      setSavingMessage('개인화 정보를 분석하는중');
      // 코드 배열을 그대로 저장 (API가 코드를 기대함)
      const d = await authService.savePersonalization({ job: pzJob ?? undefined, interests: Array.from(pzInterests) });
      // To-Be 형식 처리
      if (d?.code === "SUCCESS") {
        try { window.dispatchEvent(new CustomEvent('dw:personalization_saved')); } catch {}
        onClose();
      } else {
        alert('관심사 설정 저장에 실패했습니다.');
      }
    } catch {
      alert('관심사 설정 저장에 실패했습니다.');
    } finally { setSaving(false); setSavingMessage(null); }
  };

  if (!open) return null;

  const onSave = () => {
    try {
      // 이메일 변경 시 중복 확인 필요
      if ((email ?? '').trim() !== (originalEmail ?? '').trim()) {
        const checkedSame = (emailCheckedFor === email);
        if (!checkedSame || !emailCheckOk) {
          setEmailCheckTouched(true);
          return;
        }
      }
      
      // 이메일 중복확인 결과가 실패인 경우 저장 차단
      if (emailCheckTouched && emailCheckedFor === email && !emailCheckOk) {
        return;
      }
      // 현재 비밀번호 검증
      if (!isCurrentPwValid) { setCurrentPwTouched(true); setPwMismatch(false); return; }
      (async () => {
        try {
          setSaving(true);
          const vd = await authService.verifyPassword({ currentPassword: currentPassword });
          // To-Be 형식 처리
          if (vd?.code !== "SUCCESS") { setCurrentPwTouched(true); setPwMismatch(true); return; }
          setPwMismatch(false);
          // 프로필 업데이트 → 실패 시 종료 및 메시지 처리
          const pd = await authService.updateProfile({ name, email, phone, company });
          // To-Be 형식 처리
          if (pd?.code !== "SUCCESS") {
            const reason = pd?.details?.reason;
            if (reason === 'DUPLICATE_EMAIL') {
              setEmailCheckOk(false);
              setEmailCheckTouched(true);
              alert('이미 사용 중인 이메일입니다.');
            } else if (reason === 'INVALID_EMAIL') {
              setEmailCheckTouched(true);
              alert('올바른 이메일 형식이 아닙니다.');
            } else if (reason === 'INVALID_PHONE') {
              setPhoneTouched(true);
              alert('휴대폰 번호 형식이 올바르지 않습니다.');
            } else {
              alert('프로필 저장 중 오류가 발생했습니다.');
            }
            return;
          }
          // 로컬 스토리지 동기화
          localStorage.setItem(storageKeyPersonalOptIn, personalAgree ? "1" : "0");
          localStorage.setItem(storageKeyEmailOptIn, emailSubscribe ? "1" : "0");
          localStorage.setItem(storageKeyProfileName, name ?? "");
          localStorage.setItem(storageKeyProfileEmail, email ?? "");
          localStorage.setItem(storageKeyProfilePhone, phone ?? "");
          localStorage.setItem(storageKeyProfileCompany, company ?? "");
          // 이메일 구독 저장 → 실패 시 종료
          const ed = await authService.updateEmailSubscription({ subscribed: !!emailSubscribe });
          // To-Be 형식 처리
          if (ed?.code !== "SUCCESS") {
            alert('이메일 구독 설정 저장에 실패했습니다.');
            return;
          }
          onClose();
        } catch {
          setCurrentPwTouched(true); setPwMismatch(true); return;
        }
      })().finally(() => setSaving(false));
    } catch {}
  };

  const onEmailDuplicateCheck = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailCheckOk(false);
      setEmailCheckTouched(true);
      setEmailCheckedFor(email); // 현재 이메일로 설정하여 메시지 표시
      return;
    }
    (async () => {
      try {
        const d = await authService.checkEmail(email);
        setEmailCheckedFor(email);
        // To-Be 형식 처리: data가 true면 사용 가능
        if (d?.code === "SUCCESS" && d?.data === true) { 
          setEmailCheckOk(true); 
          setEmailCheckTouched(true); 
        } else { 
          setEmailCheckOk(false); 
          setEmailCheckTouched(true); 
        }
      } catch {
        setEmailCheckOk(false);
        setEmailCheckTouched(true);
      }
    })();
  };

  // 터치 제스처 핸들러
  const minSwipeDistance = 50;
  
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isUpSwipe = distance > minSwipeDistance;
    const isDownSwipe = distance < -minSwipeDistance;

    if (isDownSwipe) {
      // 아래로 스와이프 시 모달 닫기
      onClose();
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div 
        className="relative w-full max-w-[750px] h-full max-h-[90vh] bg-white rounded-3xl shadow-[0px_0px_18px_0px_rgba(162,166,173,0.20)] overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div data-layer="popup_프로필" className="Popup relative w-full h-full">
          {saving && (
            <div className="absolute inset-0 bg-white/60 z-[10001] flex flex-col items-center justify-center gap-3">
              <div className="animate-spin h-8 w-8 rounded-full border-4 border-black/20 border-t-black" />
              {savingMessage && (
                <div className="text-black text-base" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>{savingMessage}</div>
              )}
            </div>
          )}
          <div data-layer="Rectangle 343611" className="Rectangle343611 w-full h-[750px] left-0 top-0 absolute opacity-0" />

          {/* 헤더 */}
          <div data-layer="마이페이지" className="left-[24px] top-[24px] absolute justify-start text-black text-2xl font-medium" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>마이페이지</div>
          <button aria-label="닫기" data-layer="close" onClick={onClose} className="Close size-7 right-[24px] top-[25px] absolute text-[#65676e] hover:text-[#010103] transition-colors">
            <X className="size-7" />
          </button>

          {/* 콘텐츠 래퍼: 모바일에서는 전체 화면, 데스크톱에서는 좌측 고정 + 우측 스크롤 */}
          <div className="absolute left-0 right-0 top-[73px] bottom-0">
            {/* 우측 본문: 모바일에서는 전체, 데스크톱에서는 좌측 200px 제외 */}
            <div data-layer="Frame 3068" className="Frame3068 md:left-[200px] left-0 right-0 top-0 bottom-0 md:bottom-0 bottom-[60px] absolute overflow-y-auto overflow-x-hidden pb-8">
              <div className="relative w-full h-full">
                {/* 프로필 화면 */}
                {activeMenu === 'profile' && (
                <div className="relative w-full max-w-[460px] mx-auto h-full px-4 md:px-0">
                  <div data-layer="Rectangle 343613" className="Rectangle343613 w-full h-full left-0 top-0 absolute opacity-0 bg-white" />
                  <div data-layer="Frame 3047" className="Frame3047 size-24 md:size-32 left-1/2 -translate-x-1/2 top-[24px] absolute">
                    <img data-layer="Ellipse 600" className="Ellipse600 size-24 md:size-32 left-0 top-0 absolute rounded-full border border-gray-400" src="/svg/Ellipse-600.svg" alt="avatar" />
                  </div>

                  <div data-layer="Frame 1707482013" className="Frame1707482013 w-full left-0 top-[140px] md:top-[181px] absolute inline-flex flex-col justify-start items-start gap-6 md:gap-8 px-4 md:px-0">
                    <div data-layer="Frame 3066" className="Frame3066 self-stretch flex flex-col justify-start items-start gap-8">
                      {/* 이름 */}
                      <div data-layer="Frame 3056" className="Frame3056 self-stretch h-20 relative">
                        <div data-layer="이름" className="left-0 top-0 absolute justify-start text-gray-700 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>이름</div>
                        <div data-layer="Frame 2985" className="Frame2985 w-full h-14 px-6 py-4 left-0 top-[31px] absolute bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-400 inline-flex justify-start items-center gap-2.5">
                          <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="이름"
                            className="opacity-80 justify-start text-black text-lg font-normal bg-transparent border-none outline-none w-full"
                            style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
                          />
                        </div>
                      </div>

                      {/* 아이디(메일) */}
                      <div data-layer="Frame 3064" className="Frame3064 self-stretch h-40 md:h-24 relative">
                        <div data-layer="아이디(메일)" className="left-0 top-0 absolute justify-start text-gray-700 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>아이디(메일)</div>
                        <div className="flex flex-col md:flex-row gap-2 md:gap-0 left-0 top-[31px] absolute w-full">
                          <div data-layer="Frame 2985" className="Frame2985 flex-1 h-14 bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-400">
                            <input
                              value={email}
                              onChange={(e) => {
                                const next = e.target.value;
                                setEmail(next);
                                if (next !== originalEmail) { setEmailCheckOk(false); setEmailCheckTouched(false); }
                                else { setEmailCheckOk(true); setEmailCheckedFor(next); setEmailCheckTouched(false); }
                              }}
                              onBlur={() => {
                                const changed = (email ?? '').trim() !== (originalEmail ?? '').trim();
                                const checkedSame = (emailCheckedFor === email);
                                if (changed && (!checkedSame || !emailCheckOk)) {
                                  setEmailCheckTouched(true);
                                } else {
                                  setEmailCheckTouched(false);
                                }
                              }}
                              placeholder="이메일"
                              className="w-full h-full px-6 bg-transparent border-none outline-none text-black text-lg font-normal"
                              style={{ fontFamily: 'var(--font-noto-sans-kr)', height: '48px'}}
                            />
                          </div>
                          <button onClick={onEmailDuplicateCheck} data-layer="Frame 1707482014" className="h-14 px-4 md:px-6 py-4 bg-black rounded-lg inline-flex justify-center items-center gap-2.5 hover:bg-[#1a1a1a] transition-colors md:ml-2">
                            <div data-layer="이메일 중복" className="text-center justify-start text-white text-sm md:text-base font-medium" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>중복확인</div>
                          </button>
                        </div>
                        {/* 이메일 중복확인 결과 메시지 */}
                        {emailCheckTouched && emailCheckedFor === email && (
                          <div className="absolute left-0 top-[134px] md:top-[80px] text-sm" style={{ fontFamily: 'var(--font-noto-sans-kr)', padding: '5px' }}>
                            {emailCheckOk ? (
                              <span className="text-green-600">사용 가능한 이메일입니다.</span>
                            ) : (
                              <span className="text-red-600">
                                {/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) 
                                  ? '이미 사용 중인 이메일입니다.' 
                                  : '올바른 이메일 형식이 아닙니다.'
                                }
                              </span>
                            )}
                          </div>
                        )}
                        {(email !== originalEmail && (!emailCheckOk || emailCheckedFor !== email) && !emailCheckTouched) && (
                          <div className="absolute left-0 top-[80px] text-sm text-red-600" style={{ fontFamily: 'var(--font-noto-sans-kr)', padding: '5px' }}>
                            이메일 중복 확인을 해주세요.
                          </div>
                        )}
                      </div>

                      {/* 휴대폰 번호 */}
                      <div data-layer="Frame 3068" className="Frame3068 self-stretch h-25 relative">
                        <div data-layer="휴대폰 번호" className="left-0 top-0 absolute justify-start text-gray-700 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>휴대폰 번호</div>
                        <div data-layer="Frame 2985" className="Frame2985 w-full h-14 px-6 py-4 left-0 top-[31px] absolute bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-400 inline-flex justify-start items-center gap-2.5">
                          <input
                            type="tel"
                            value={phone}
                            onChange={(e) => {
                              const digits = e.target.value.replace(/\D/g, "");
                              if (digits.length > 11) return;
                              const must = "010";
                              for (let i = 0; i < Math.min(3, digits.length); i++) {
                                if (digits[i] !== must[i]) return;
                              }
                              if (digits.length >= 3 && !digits.startsWith('010')) return;
                              setPhone(digits);
                            }}
                            onBlur={() => setPhoneTouched(true)}
                            placeholder="휴대폰 번호"
                            className="opacity-80 justify-start text-black text-lg font-normal bg-transparent border-none outline-none w-full"
                            style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
                            aria-invalid={!isPhoneValid && phoneTouched}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={11}
                          />
                        </div>
                        {/* phone helper (placed inside phone container) */}
                        <div className="absolute left-0 top-[80px] text-sm" style={{ fontFamily: 'var(--font-noto-sans-kr)', padding: '5px' }}>
                          {!isPhoneValid && phoneTouched ? (
                            <span className="text-red-600">010으로 시작하는 11자리 숫자만 입력해주세요.</span>
                          ) : (
                            <span className="text-zinc-400">예: 01012345678</span>
                          )}
                        </div>
                      </div>

                      {/* 비밀번호 */}
                      <div data-layer="Frame 3067" className="Frame3067 self-stretch h-20 relative mb-[15px]">
                        <div data-layer="비밀번호" className="left-0 top-0 absolute justify-start text-gray-700 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>비밀번호</div>
                        <button
                          onClick={async () => {
                            if (!isCurrentPwValid) { setCurrentPwTouched(true); setPwMismatch(false); return; }
                            try {
                              const d = await authService.verifyPassword({ currentPassword: currentPassword });
                              // To-Be 형식 처리
                              if (d?.code === "SUCCESS") { setPwMismatch(false); setShowChangePw(true); return; }
                              // 불일치: 헬퍼 위치에 에러 메시지 노출
                              setCurrentPwTouched(true); setPwMismatch(true);
                            } catch {
                              setCurrentPwTouched(true); setPwMismatch(true);
                            }
                          }}
                          data-layer="변경하기"
                          className="left-[398px] top-0 absolute text-right justify-start text-gray-700 text-base font-normal underline hover:text-black"
                          style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
                          aria-disabled={!isCurrentPwValid}
                        >
                          변경하기
                        </button>
                        {/* 현재 비밀번호 입력 */}
                        <div className="w-full h-14 pl-6 pr-12 py-4 left-0 top-[31px] absolute bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-400 flex items-center gap-2.5">
                          <input
                            type={pwVisible ? 'text' : 'password'}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            onBlur={() => setCurrentPwTouched(true)}
                            placeholder="현재 비밀번호"
                            className="opacity-80 justify-start text-black text-lg font-normal bg-transparent border-none outline-none w-full"
                            style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
                            aria-invalid={!isCurrentPwValid && currentPwTouched}
                          />
                          <button type="button" aria-label={pwVisible ? "비밀번호 숨기기" : "비밀번호 보기"} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-[#010103] transition-colors"
                            onClick={() => setPwVisible(v => !v)}>
                            {pwVisible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                          </button>
                        </div>
                        {(!isCurrentPwValid && currentPwTouched) && (
                          <div className="absolute left-0 top-[80px] text-sm text-red-600" style={{ fontFamily: 'var(--font-noto-sans-kr)', padding: '5px' }}>
                            현재 비밀번호를 입력해주세요.
                          </div>
                        )}
                        {(pwMismatch && currentPwTouched) && (
                          <div className="absolute left-0 top-[80px] text-sm text-red-600" style={{ fontFamily: 'var(--font-noto-sans-kr)', padding: '5px' }}>
                            현재 비밀번호가 일치하지 않습니다.
                          </div>
                        )}
                      </div>

                      {/* 소속 회사 */}
                      <div data-layer="Frame 3065" className="Frame3065 self-stretch h-20 relative">
                        <div data-layer="소속 회사" className="left-0 top-0 absolute justify-start text-gray-700 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>소속 회사</div>
                        <div data-layer="Frame 2985" className="Frame2985 w-full h-14 px-6 py-4 left-0 top-[31px] absolute bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-400 inline-flex justify-start items-center gap-2.5">
                          <input
                            value={company}
                            onChange={(e) => setCompany(e.target.value)}
                            placeholder="소속 회사"
                            className="opacity-80 justify-start text-black text-lg font-normal bg-transparent border-none outline-none w-full"
                            style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* 동의 토글들 */}
                    <div data-layer="Frame 1707482017" className="Frame1707482017 size- flex flex-col justify-start items-start gap-4">
                      {/* 이메일 구독 알림 수신 */}
                      <div data-layer="Frame 1707481861" className="Frame1707481861 size- inline-flex justify-start items-center gap-4">
                        <button aria-pressed={emailSubscribe} onClick={() => setEmailSubscribe(v => !v)} data-layer="Toggle_basic" data-property-1="Default" className="ToggleBasic size- flex justify-start items-start gap-2">
                          <div data-layer="ToggleContainer" className="Togglecontainer w-12 h-6 relative overflow-hidden">
                            <div data-layer="Background" className={`Background w-12 h-6 left-0 top-0 absolute rounded-full ${emailSubscribe ? 'bg-blue-700' : 'bg-gray-300'}`} />
                            <div data-layer="Big" className={`Big size-4 top-[4px] absolute bg-white rounded-full transition-all ${emailSubscribe ? 'left-[28px]' : 'left-[4px]'}`} />
                          </div>
                        </button>
                        <div data-layer="Frame 1707481860" className="Frame1707481860 size- flex justify-start items-center gap-1">
                          <div data-layer="이메일 구독 알림 수신" className="justify-start text-gray-700 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>이메일 구독 알림 수신</div>
                          <div data-layer="(선택)" className="justify-start text-zinc-500 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>(선택)</div>
                        </div>
                      </div>
                    </div>

                    {/* 저장/탈퇴 */}
                    <div data-layer="Frame 1707482012" className="Frame1707482012 self-stretch flex flex-col justify-start items-start gap-10">
                      <button 
                        onClick={onSave} 
                        disabled={
                          !isPhoneValid || 
                          (emailCheckTouched && emailCheckedFor === email && !emailCheckOk)
                        } 
                        data-layer="Frame 2860" 
                        className="Frame2860 self-stretch h-14 px-8 py-3 bg-black rounded-lg inline-flex justify-center items-center gap-2.5 hover:bg-[#1a1a1a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <div data-layer="저장하기" className="justify-start text-white text-lg font-semibold" style={{ fontFamily: 'var(--font-pretendard)' }}>저장하기</div>
                      </button>
                      <div data-layer="Frame 1707481919" className="Frame1707481919 self-stretch flex flex-col justify-start items-start gap-6">
                        <div data-layer="Vector 677" className="Vector677 w-[460px] h-0 outline outline-1 outline-offset-[-0.50px] outline-zinc-200" />
                        <button type="button" onClick={() => alert('탈퇴 기능은 추후 지원 예정입니다.')} data-layer="Frame 3059" className="Frame3059 self-stretch h-5 relative text-left hover:opacity-80 mb-[30px]">
                          <div data-layer="탈퇴하기" className="left-0 top-0 absolute justify-start text-gray-700 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>탈퇴하기</div>
                          <img data-layer="Vector 646" src="/svg/Vector%20646.svg" alt="arrow" className="Vector646 w-5 h-4 right-0 top-[1px] absolute" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                )}

                {/* 관심사 설정 화면 */}
                {activeMenu === 'personalization' && (
                  <div data-layer="Frame 3063" className="Frame3063 relative w-full h-auto px-4 md:px-10 pb-8">
                    <div data-layer="Frame 1707481754" className="Frame1707481754 w-full max-w-[452px] mx-auto mt-10 inline-flex flex-col justify-start items-start gap-4">
                      <div data-layer="직무" className="self-stretch justify-start text-gray-700 text-base font-normal" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>직무</div>
                      <div ref={pzDropdownRef} data-layer="select_직무" className="w-full">
                        <div className="w-full h-14 relative bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-400 cursor-pointer flex items-center justify-between px-6" onClick={() => setPzDropdownOpen(v => !v)}>
                          <div className="text-lg" style={{ fontFamily: 'var(--font-noto-sans-kr)', color: pzJob ? '#000' : '#9ca3af' }}>
                            {pzJob ? (jobCodeToName[pzJob] ?? pzJob) : '직무를 선택해주세요'}
                          </div>
                          <div className="flex items-center justify-center w-4 h-4">
                            <div className="w-2.5 h-2.5 border-b border-r border-gray-600 rotate-45 transform translate-y-[-1px]" />
                          </div>
                        </div>
                        {pzDropdownOpen && (
                          <div className="mt-2 max-h-56 overflow-auto bg-white rounded-lg outline outline-1 outline-gray-200 shadow-sm">
                            {jobList.map(job => (
                              <button 
                                key={job.code} 
                                type="button" 
                                onClick={() => { setPzJob(job.code); setPzDropdownOpen(false); }} 
                                className={`w-full text-left px-4 py-2 hover:bg-zinc-100 text-[16px] ${
                                  pzJob === job.code ? 'bg-blue-50 text-blue-600' : ''
                                }`} 
                                style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
                              >
                                {job.codeName}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div data-layer="Frame 1707481982" className="Frame1707481982 w-full max-w-[460px] mx-auto mt-8">
                      <div data-layer="관심사 설정" className="justify-start text-gray-700 text-base font-normal mb-3" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>관심사 설정</div>
                      <div data-layer="Frame 1707481981" className="Frame1707481981 w-full">
                        <div className="flex flex-wrap gap-2 md:gap-3 w-full">
                          {interestList.map((item) => {
                            const code = item.code;
                            const label = item.valueText;
                            const isSelected = pzInterests.has(code);
                            return (
                              <button
                                key={code}
                                type="button"
                                onClick={() => toggleInterest(code)}
                                className={`${isSelected ? 'bg-black text-white' : 'bg-neutral-100 text-zinc-400 outline outline-1 outline-zinc-200'} rounded-[20px] px-3 md:px-5 py-2 md:py-2.5 text-sm md:text-base font-medium hover:opacity-90 transition-colors`}
                                style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="w-full max-w-[460px] mx-auto mt-10 flex justify-end">
                      <button onClick={onSavePersonalization} className="h-14 px-8 py-3 bg-black rounded-lg inline-flex justify-center items-center gap-2.5 text-white text-lg font-semibold hover:bg-[#1a1a1a] transition-colors" style={{ fontFamily: 'var(--font-pretendard)' }}>저장하기</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* 좌측 사이드: 데스크톱에서만 표시 */}
          <div data-layer="Frame 3062" className="Frame3062 hidden md:flex w-[200px] left-0 top-[73px] bottom-0 absolute flex-col bg-neutral-100/50">
            <div className="flex flex-col p-4 space-y-2 flex-grow">
              {/* 프로필 버튼 */}
              <button type="button" onClick={() => setActiveMenu('profile')} data-layer="button_목차" className={`Button w-full pl-3 pr-6 py-2.5 rounded-lg inline-flex justify-start items-center gap-2.5 hover:opacity-100 transition-colors ${activeMenu === 'profile' ? 'bg-gray-200' : ''}`}>
                <div data-layer="프로필" className="justify-start text-base font-medium leading-snug" style={{ fontFamily: 'var(--font-noto-sans-kr)', color: (activeMenu === 'profile' ? '#010103' : '#000000') }}>프로필</div>
              </button>
              
              {/* 관심사 설정 버튼 */}
              <button type="button"
                onClick={() => setActiveMenu('personalization')}
                className={`Button w-full pl-3 pr-6 py-2.5 rounded-lg inline-flex justify-start items-center gap-2.5 hover:opacity-100 transition-colors ${activeMenu === 'personalization' ? 'bg-gray-200' : ''}`}
                style={{ fontFamily: 'var(--font-noto-sans-kr)', color: (activeMenu === 'personalization' ? '#010103' : '#000000') }}
                aria-pressed={activeMenu === 'personalization'}
              >
                <div>관심사 설정</div>
              </button>
            </div>
            
            {/* 로그아웃 버튼 - 하단 고정 */}
            <div data-layer="Frame 3061" className="p-4 pl-7 mt-auto">
              <button onClick={onLogout} data-layer="로그아웃" className="flex items-center justify-between w-full text-black text-base font-medium hover:opacity-80" style={{ fontFamily: 'var(--font-noto-sans-kr)' }}>
                <span>로그아웃</span>
                <div data-layer="entrance" className="Entrance size-6">
                  <img src="/svg/entrance.svg" alt="entrance" className="w-6 h-6" />
                </div>
              </button>
            </div>
          </div>

          {/* 모바일 하단 탭 네비게이션 */}
          <div className="md:hidden absolute bottom-0 left-0 right-0 bg-white border-t border-gray-200 flex">
            <button 
              type="button" 
              onClick={() => setActiveMenu('profile')} 
              className={`flex-1 py-4 text-center text-sm font-medium transition-colors ${activeMenu === 'profile' ? 'text-black border-b-2 border-black' : 'text-gray-500'}`}
              style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
            >
              프로필
            </button>
            <button 
              type="button" 
              onClick={() => setActiveMenu('personalization')} 
              className={`flex-1 py-4 text-center text-sm font-medium transition-colors ${activeMenu === 'personalization' ? 'text-black border-b-2 border-black' : 'text-gray-500'}`}
              style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
            >
              관심사 설정
            </button>
            <button 
              onClick={onLogout} 
              className="flex-1 py-4 text-center text-sm font-medium text-gray-500 hover:text-black transition-colors"
              style={{ fontFamily: 'var(--font-noto-sans-kr)' }}
            >
              로그아웃
            </button>
          </div>

          {/* 구분선 */}
          <div data-layer="Vector 676" className="Vector676 w-[750px] h-0 left-0 top-[73px] absolute outline outline-1 outline-offset-[-0.50px] outline-zinc-200" />
          {/* Removed right-side decorative bar to avoid confusion */}

          {/* 비밀번호 변경 모달 */}
          <PasswordChangePromptModal open={showChangePw} onClose={() => setShowChangePw(false)} onChange={() => setShowChangePw(false)} currentPassword={currentPassword} isFirstLogin={false} isInitialPassword={false} hidePostponeButton={true} userIdx={userId} />
        </div>
      </div>
    </div>,
    typeof window !== 'undefined' ? document.body : (globalThis as any)?.document?.body ?? ({} as any)
  );
}


